import { useCallback, useEffect, useState } from 'react';
import { isIos, isStandalone } from '../services/push';

/** A Chrome/Edge által kiváltott telepítési esemény (nincs a lib.dom-ban). */
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

declare global {
  interface Window {
    __ctInstallPrompt: BeforeInstallPromptEvent | null;
  }
}

export type InstallMode =
  /** Már telepítve van (kezdőképernyőről / önálló ablakból fut). */
  | 'installed'
  /** Egy kattintással telepíthető (Chrome, Edge, Android). */
  | 'prompt'
  /** iPhone / iPad: csak kézzel, a Megosztás menüből. */
  | 'ios-manual'
  /** A böngésző nem támogatja a telepítést (pl. asztali Firefox). */
  | 'unsupported';

export interface InstallPromptState {
  mode: InstallMode;
  /** Elindítja a böngésző telepítési párbeszédét. */
  install: () => Promise<'accepted' | 'dismissed' | 'unavailable'>;
  busy: boolean;
}

/**
 * A PWA telepítés állapota és indítása.
 *
 * A telepítési párbeszédet csak a Chrome/Edge motor teszi elérhetővé
 * (beforeinstallprompt). A Safari - és így az iPhone - nem ad rá API-t,
 * ott a felhasználónak a Megosztás menüből kell hozzáadnia a kezdőképernyőhöz.
 */
export const useInstallPrompt = (): InstallPromptState => {
  const [hasPrompt, setHasPrompt] = useState(() => window.__ctInstallPrompt != null);
  const [installed, setInstalled] = useState(() => isStandalone());
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const onPromptAvailable = () => setHasPrompt(true);
    const onInstalled = () => {
      setHasPrompt(false);
      setInstalled(true);
    };

    window.addEventListener('ct:installprompt', onPromptAvailable);
    window.addEventListener('ct:appinstalled', onInstalled);

    // Ha közben a kezdőképernyőről nyitották meg
    const media = window.matchMedia('(display-mode: standalone)');
    const onDisplayModeChange = () => setInstalled(isStandalone());
    media.addEventListener('change', onDisplayModeChange);

    return () => {
      window.removeEventListener('ct:installprompt', onPromptAvailable);
      window.removeEventListener('ct:appinstalled', onInstalled);
      media.removeEventListener('change', onDisplayModeChange);
    };
  }, []);

  const install = useCallback(async () => {
    const event = window.__ctInstallPrompt;
    if (!event) {
      return 'unavailable' as const;
    }

    setBusy(true);
    try {
      await event.prompt();
      const { outcome } = await event.userChoice;

      // A félretett esemény csak egyszer használható fel
      window.__ctInstallPrompt = null;
      setHasPrompt(false);

      return outcome;
    } finally {
      setBusy(false);
    }
  }, []);

  const mode: InstallMode = installed
    ? 'installed'
    : hasPrompt
      ? 'prompt'
      : isIos()
        ? 'ios-manual'
        : 'unsupported';

  return { mode, install, busy };
};
